import { Action, configureStore } from '@reduxjs/toolkit';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { createEpicMiddleware } from 'redux-observable';

import { baseApi } from './api';
import App, { router } from './App';
import { ENABLE_MSW, IS_DEV } from './env';
import { ensureCoastVisitorId } from './general/utils/coast-visitor-id';
import reportWebVitals from './reportWebVitals';
import { createServices, Services } from './services';
import { initializeDataDogRUM } from './services/datadog';
import createRootReducer, { RootState } from './store';
import { rootEpic } from './store/epics';
import { worker } from './test/mocks/browser';

initializeDataDogRUM();
ensureCoastVisitorId();

const epicMiddleware = createEpicMiddleware<Action, Action, RootState, Services>({
  dependencies: createServices({ router }),
});

const store = configureStore({
  reducer: createRootReducer(),
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(epicMiddleware).concat(baseApi.middleware),
});

epicMiddleware.run(rootEpic);

const ReduxApp: React.FC = () => (
  <Provider store={store}>
    <App />
  </Provider>
);

// If MSW is enabled, wait for it before rendering the app in order to prevent connection refused errors.
const renderApp = () => {
  const container = document.getElementById('root');
  const root = createRoot(container!);
  root.render(
    <React.StrictMode>
      <ReduxApp />
    </React.StrictMode>,
  );
};

if (IS_DEV && ENABLE_MSW) {
  worker
    .start({ onUnhandledRequest: 'bypass' })
    .then(renderApp)
    .catch((error) => console.log(error));
} else {
  renderApp();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Comment to bump UI
